import {
  AVAILABLE_CURRENCIES,
  DEFAULT_CURRENCY,
  DOMAIN_MAP_TO_CURRENCY,
} from "@configs/currencies";

const CURRENCY_OVERRIDE_KEY = "__currencyOverride";

/**
 * QA/preview override: force the storefront currency via `?currency=<code>`
 * (e.g. `?currency=pln`). Persists for the tab session so it survives the
 * multi-step checkout navigation. Returns undefined when unset so callers fall
 * back to domain-based detection. Only honours codes in AVAILABLE_CURRENCIES.
 */
export const getCurrencyOverride = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  try {
    const q = new URLSearchParams(window.location.search).get("currency");
    if (q && AVAILABLE_CURRENCIES.includes(q.toLowerCase())) {
      const value = q.toLowerCase();
      window.sessionStorage.setItem(CURRENCY_OVERRIDE_KEY, value);
      return value;
    }
    const stored = window.sessionStorage.getItem(CURRENCY_OVERRIDE_KEY);
    if (stored && AVAILABLE_CURRENCIES.includes(stored)) return stored;
  } catch {
    /* sessionStorage/URL unavailable — ignore and use domain detection */
  }
  return undefined;
};

export const getDomain = (): string => {
  if (typeof window !== "undefined") {
    return window.location.hostname; // Gets the current domain (hostname)
  }
  return "";
};

export const getTopLevelDomain = (): string => {
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;
    const domainParts = hostname.split(".");

    // Return the last part, which is the TLD
    return domainParts.length > 1
      ? `${domainParts[domainParts.length - 1]}`
      : "";
  }

  return "";
};

/**
 * The market whose prices and shipping a non-host currency belongs to. "eur"
 * has no single market of its own (si, hr, sk and de all charge eur), so it is
 * resolved against the host below.
 */
const MARKET_BY_OVERRIDE_CURRENCY: Record<string, string> = {
  "eur-at": "at",
  czk: "cz",
  pln: "pl",
  ron: "ro",
};

/**
 * The market key ("si", "pl", "ro"…) for a host TLD and an optional
 * `?currency=` override. Pure, so the rule is testable without a window.
 *
 * Without an override this is the TLD, exactly as before. With one, the market
 * follows the forced currency — a `?currency=pln` walk on the .si stage host
 * must see the PL free-shipping threshold, COD fee and shipping country, not
 * Slovenia's. An eur override keeps an eur host (hr stays hr) and otherwise
 * means si, the default eur shop.
 */
export const marketForHost = (
  tld: string,
  override: string | undefined | null,
): string => {
  if (!override) return tld;
  const hostCurrency =
    DOMAIN_MAP_TO_CURRENCY[tld as keyof typeof DOMAIN_MAP_TO_CURRENCY];
  if (hostCurrency === override) return tld;
  if (override === DEFAULT_CURRENCY) return "si";
  return MARKET_BY_OVERRIDE_CURRENCY[override] ?? tld;
};

/**
 * The shop's market key, honouring the `?currency=` QA override. Use this —
 * not getTopLevelDomain — for anything priced or shipped per market (shipping
 * cost, free-shipping threshold, COD fee, default shipping country). The raw
 * TLD stays right for things tied to the host itself, like the Meta pixel.
 */
export const getMarketDomain = (): string =>
  marketForHost(getTopLevelDomain(), getCurrencyOverride());

export const getCurrencyBasedOnDomain = (): string => {
  const override = getCurrencyOverride();
  if (override) return override;
  const topLevelDomain = getTopLevelDomain();
  if (!topLevelDomain) {
    return DEFAULT_CURRENCY;
  }
  if (topLevelDomain in DOMAIN_MAP_TO_CURRENCY) {
    return DOMAIN_MAP_TO_CURRENCY[
      topLevelDomain as keyof typeof DOMAIN_MAP_TO_CURRENCY
    ];
  }
  return DEFAULT_CURRENCY;
};

/**
 * TLD -> app locale. Each brand runs on its own country domain; this maps the
 * domain back to the locale the app should enter at when a visitor lands on
 * the bare root `/`.
 */
export const LOCALE_BY_TLD: { [tld: string]: string } = {
  si: "sl",
  hr: "hr",
  sk: "sk",
  cz: "cs",
  pl: "pl",
  at: "de-AT",
};

/**
 * Extract the TLD from an HTTP `Host` header value (server-safe, no `window`),
 * e.g. "www.foto-chwila.pl:3334" -> "pl".
 */
export const getTldFromHost = (host: string): string => {
  const hostname = (host || "").split(":")[0];
  const parts = hostname.split(".");
  return parts.length > 1 ? parts[parts.length - 1].toLowerCase() : "";
};

/**
 * Resolve the app locale for an HTTP `Host` header. Returns undefined for hosts
 * without a recognised brand TLD (localhost, preview deploys) so callers can
 * fall back to the default locale.
 */
export const getLocaleByHost = (host: string): string | undefined => {
  return LOCALE_BY_TLD[getTldFromHost(host)];
};

/**
 * TLD -> marketing-site apex host. The shop runs on per-brand subdomains
 * (nakup.fotodelavnica.si, narudzba.fotoradnja.hr, ...), but each brand has a
 * dedicated marketing site on the apex domain that is the canonical SEO
 * surface and already ranks for the brand terms. The shop's indexable pages
 * point their rel=canonical here so the two storefronts don't compete for the
 * same brand queries. Keep in sync with the BRANDS list in app/sitemap.xml.
 */
export const APEX_HOST_BY_TLD: { [tld: string]: string } = {
  si: "fotodelavnica.si",
  hr: "fotoradnja.hr",
  sk: "fotospomienky.sk",
  cz: "fotkavzpominek.cz",
  pl: "foto-chwila.pl",
  at: "fotokreation.at",
};

/**
 * Marketing-site apex host for an HTTP `Host` header (server-safe, no
 * `window`). Returns undefined for hosts without a recognised brand TLD
 * (localhost, preview/stage deploys) so callers can skip emitting a
 * cross-domain canonical there.
 */
export const getMarketingApexByHost = (host: string): string | undefined => {
  return APEX_HOST_BY_TLD[getTldFromHost(host)];
};
